import "./PostTimeline.scss";

import { useEffect, useRef, useState } from "react";
import Post, { PostInfo } from "../../../components/post/Post";
import UploadingPost from "../../../components/uploading-post/UploadingPost";
import { PostApiRoutes } from "../../../shared/api-routes/post/post.api-routes";
import http from "../../../shared/utils/http";
import CustomDropdown from "../components/aced-dropdown/AcedDropdown";
import WritePostBar from "../components/write-post/WritePostBar";

const PostTimeline: React.FC = () => {
	const [isCreatePostModalOpen, setCreatePostModal] = useState<boolean>(false);

	const [posts, setPosts] = useState<PostInfo[]>([]);
	const [uploadingPosts, setUploadingPosts] = useState<PostInfo[]>([]);
	const [cursor, setCursor] = useState<string | null>(null);
	const [hasMore, setHasMore] = useState(true);
	const [loading, setLoading] = useState(false);
	const loaderRef = useRef<HTMLDivElement | null>(null);

	const postLimit: number = 10;

	const onCreatePost = (postInfo: PostInfo) => {
		// If post has media that's processing, add to uploading section
		if (postInfo.mediaStatus === "processing") {
			setUploadingPosts((prev) => [postInfo, ...prev]);
		} else {
			// Otherwise add to main timeline
			setPosts((prev) => [postInfo, ...prev]);
		}
	};

	// Callback when a post's status changes from processing to ready
	const onPostReady = (postId: string, updatedPost: PostInfo) => {
		// Remove from uploading section
		setUploadingPosts((prev) => prev.filter((p) => p.id !== postId));
		// Add to main timeline
		setPosts((prev) => [updatedPost, ...prev]);
	};

	const loadMorePosts = async () => {
		if (loading || !hasMore) return;

		setLoading(true);
		http.get(PostApiRoutes.GET_FEED(postLimit, cursor)).then((res) => {
			if (res.length === 0) {
				setHasMore(false);
			} else {
				setPosts((prev) => [...prev, ...res]);
				const lastPost = res[res.length - 1];
				setCursor(lastPost.createdAt);
			}
			setLoading(false);
		});
	};

	useEffect(() => {
		if (!loaderRef.current || !hasMore) return;

		const observer = new IntersectionObserver(
			(entries) => {
				if (entries[0].isIntersecting && !loading) {
					loadMorePosts();
				}
			},
			{
				rootMargin: "300px",
			},
		);

		observer.observe(loaderRef.current);

		return () => observer.disconnect();
	}, [loaderRef.current, hasMore, loading]);

	// const [selectedOrderStyle, setSelectedOrderStyle] = useState("");

	// const handleChange = (event: any) => {
	//   setSelectedOrderStyle(event.target.value);
	// };

	const onDeletePost = (post_id: string) => {
		setPosts((prev) => prev.filter((item) => item.id !== post_id));
	};

	return (
		<div className="post_timeline_container">
			<WritePostBar
				createPostModalState={{ isCreatePostModalOpen, setCreatePostModal }}
				onCreatePost={onCreatePost}
			/>
			<div className="dropdown-div">
				<CustomDropdown
					label="Order by"
					options={[
						{ value: "recent", label: "Recent" },
						//{ value: "relevant", label: "More relevant" },
					]}
					defaultValue="Recent"
				/>
			</div>

			{/* Uploading posts section */}
			{uploadingPosts.length > 0 && (
				<div className="uploading-posts-section">
					{uploadingPosts.map((post: PostInfo) => {
						return (
							<UploadingPost
								postInfo={post}
								key={post.id}
								onReady={onPostReady}
							/>
						);
					})}
				</div>
			)}

			{/* Main timeline */}
			<div className="post-timeline">
				{posts.map((post: PostInfo) => (
					<Post
						postInfo={post}
						key={post.id}
						onDeletePost={onDeletePost}
					/>
				))}
			</div>
			{hasMore && (
				<div
					ref={loaderRef}
					style={{ height: "1px" }}
				/>
			)}
		</div>
	);
};

export default PostTimeline;
